// src/features/hmi-simulator/services/hmiDataProvider.ts
import { HttpError } from 'react-admin';

const API_URL = process.env.REACT_APP_API_URL;

interface HMICredentials {
  apiKey: string;
  deviceId: string;
  secretKey?: string;
}

class HMIDataProvider {
  private credentials: HMICredentials | null = null;

  setCredentials(credentials: HMICredentials) {
    this.credentials = credentials;
    sessionStorage.setItem('hmi_credentials', JSON.stringify(credentials));
  }

  getCredentials(): HMICredentials | null {
    if (!this.credentials) {
      const stored = sessionStorage.getItem('hmi_credentials');
      if (stored) {
        this.credentials = JSON.parse(stored);
      }
    }
    return this.credentials;
  }

  clearCredentials() {
    this.credentials = null;
    sessionStorage.removeItem('hmi_credentials');
  }

  private async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
   
    
    const baseUrl = API_URL.replace(/\/api\/v1\/ui\/?$/, ''); 
    const url = `${baseUrl}/api/v1/hmi${endpoint}`;
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Añadir headers de autenticación HMI si existen credenciales
    // EXCEPTO para el endpoint de provision
    if (this.credentials && !endpoint.includes('/provision')) {
      headers['X-API-Key'] = this.credentials.apiKey;
      headers['X-Device-ID'] = this.credentials.deviceId;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      const text = await response.text();
      const json = text ? JSON.parse(text) : {};

      if (!response.ok) {
        throw new HttpError(
          json.message || json.error_message || `Error ${response.status}`,
          response.status,
          json
        );
      }

      return json;
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new HttpError(
        error.message || 'Network error',
        500
      );
    }
  }

  // Métodos específicos para HMI

  async provision(provisioningCode: string) {
    const response = await this.request('/provision', {
      method: 'POST',
      body: JSON.stringify({ provisioningCode }),
    });

    // Guardar credenciales automáticamente después del provisioning
    if (response.apiKey && response.deviceId) {
      this.setCredentials({
        apiKey: response.apiKey || response.ApiKey,
        deviceId: response.deviceId || response.DeviceId,
        secretKey: response.secretKey || response.SecretKey,
      });
    }

    return response;
  }

  async getConfig() {
    return this.request('/config', {
      method: 'GET',
    });
  }

  async getControllerConfig() {
    return this.request('/hmi-controller-config', {
      method: 'GET',
    });
  }

  async createEmailAssignment(email: string) {
    return this.request('/auth/email', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async validateCode(code: string) {
    return this.request('/validate-code', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async confirmLocker(lockerName: string, idNumber: string) {
    return this.request('/locker/confirm', {
      method: 'POST',
      body: JSON.stringify({ 
        lockerName, 
        idNumber,
        isManual: false 
      }),
    });
  }

  async checkoutLocker(lockerName: string, idNumber: string) {
    return this.request('/locker/checkout', {
      method: 'POST',
      body: JSON.stringify({ 
        lockerName, 
        idNumber,
        isManual: false 
      }),
    });
  }

  async staffLogin(password: string) {
    return this.request('/staff/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  async staffOpenLocker(lockerName: string, sessionToken: string) {
    return this.request('/staff/open-locker', {
      method: 'POST',
      body: JSON.stringify({ 
        lockerName, 
        sessionToken 
      }),
    });
  }

  async getLockersStatus(sessionToken: string) {
    return this.request(`/lockers/status?sessionToken=${sessionToken}`, {
      method: 'GET',
    });
  }

  async sendHeartbeat(data?: any) {
  return this.request('/heartbeat', {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });
}

  async reportControllerHeartbeat(controllerIpAddress: string, controllerTcpPort: number) {
    return this.request('/report-controller-heartbeat', {
      method: 'POST',
      body: JSON.stringify({
        ControllerIpAddress: controllerIpAddress,  // PascalCase
        ControllerTcpPort: controllerTcpPort,  
      }),
    });
  }
}

export const hmiDataProvider = new HMIDataProvider();